import {
  AT_ONCE,
  DEADLINE_MS,
  ask,
  callerFor,
  inBatches,
  newLadder,
} from '@asvise/model/client'
import type { LoadedDocument } from '@asvise/read'
import type { Gate, RawBenefit, RawPolicy, ReadOutcome } from '@asvise/household-extract/types'
import { sessionToken } from '../session-token'

/**
 * Driving a read from the browser, which is where the waiting belongs.
 *
 * Nothing here is knowledge. Which shape of request to try, how long to give
 * each, and how many to have outstanding is `@asvise/model/client`, shared with
 * the commercial reader because it would be the same code for any endpoint
 * answering any question. What is left in this file is which passes this reader
 * makes and in what order, which is two sentences long.
 *
 * A read is allowed half an hour. No HTTP request survives that, which is the
 * whole reason the waiting is out here and not in a function.
 */
const ENDPOINT = '/extract'

/** This app's own origin, carrying this app's session and never a model key. */
const call = callerFor(ENDPOINT, sessionToken)

/**
 * Which rung answered, for as long as the page is open.
 *
 * An endpoint's structured-output support is a property of the endpoint rather
 * than of the question, so discovering it once and keeping it is the difference
 * between a document costing one discovery and costing one per benefit.
 */
const ladder = newLadder()

/** Only for tests, which would otherwise inherit each other's discoveries. */
export function forgetLadder(): void {
  ladder.from = 0
  ladder.proven = false
}

/** What a detail pass names: one benefit, as the index pass reported it. */
type Detail = Pick<RawBenefit, 'code' | 'verbatim' | 'operative'>

/**
 * Is this file readable at all, asked before anything is spent on it.
 *
 * Cheaper to hear now than after ten calls. The reason is the server's, because
 * recognising a pack with no schedule in it is reading the document.
 */
export async function planDocument(
  doc: LoadedDocument,
  signal?: AbortSignal,
): Promise<{ summary: string; gate: Gate }> {
  const response = await call('plan', { doc }, signal)
  const said = (await response.json().catch(() => null)) as {
    summary?: string
    gate?: Gate
    error?: string
  } | null

  if (!response.ok || said?.summary === undefined || said.gate === undefined) {
    throw new Error(said?.error ?? `This file could not be read (${response.status}).`)
  }
  return { summary: said.summary, gate: said.gate }
}

/**
 * Two passes: what benefits are here, then what each one states.
 *
 * The index pass can fail the read, because a schedule with no benefits read
 * from it is nothing anybody can review. A detail pass that fails costs that
 * benefit its detail and no more: the row survives with its code, its quote and
 * whether the policy grants it, and every field it did not get stays unread
 * rather than becoming an answer.
 *
 * What comes back is the reply, not the reading. Nothing here has been found in
 * the document yet; that is `verifyDocument`, and it is the only thing that
 * turns any of this into something a person may see.
 */
export async function proposePolicy(
  doc: LoadedDocument,
  signal?: AbortSignal,
): Promise<RawPolicy> {
  const clock = new AbortController()
  const deadline = setTimeout(() => clock.abort(), DEADLINE_MS)
  const stop = () => clock.abort()
  signal?.addEventListener('abort', stop)
  const trouble: string[] = []

  try {
    let indexed = await ask<RawPolicy, Detail>(
      { pass: 'index' },
      doc,
      clock,
      signal,
      trouble,
      'the benefits',
      ladder,
      call,
    )

    /**
     * Once, and only for this pass.
     *
     * A gateway routing to a dead provider is fixed by asking again. A detail
     * pass that fails costs one benefit's detail; this one costs the document.
     */
    if (indexed.read === undefined && !clock.signal.aborted) {
      trouble.push('asking again')
      indexed = await ask<RawPolicy, Detail>(
        { pass: 'index' },
        doc,
        clock,
        signal,
        trouble,
        'the benefits, again',
        ladder,
        call,
      )
    }

    const policy = indexed.read
    if (policy === undefined) {
      if (clock.signal.aborted && !signal?.aborted) {
        throw new Error(
          `The model endpoint did not answer within ${DEADLINE_MS / 60_000} minutes. ${trouble.join('; ')}`.trim(),
        )
      }
      throw new Error(`The model endpoint could not be read. ${trouble.join('; ')}`)
    }

    const benefits = await inBatches(policy.benefits ?? [], AT_ONCE, async (benefit) => {
      /**
       * A benefit the schedule shows as not included is not asked about.
       *
       * There is nothing for it to state, and a pass asking what its sum is
       * invites a model to find one.
       */
      if (!benefit.operative) return benefit

      const stated = await ask<RawBenefit, Detail>(
        {
          pass: 'detail',
          benefit: {
            code: benefit.code,
            verbatim: benefit.verbatim,
            operative: benefit.operative,
          },
        } as never,
        doc,
        clock,
        signal,
        trouble,
        benefit.code.toLowerCase().replace(/_/g, ' '),
        ladder,
        call,
      )

      /**
       * Replaced whole rather than merged. What the index pass settled is
       * settled: the endpoint is given the code, the quote and whether the
       * benefit is granted, and hands all three back untouched. Merging here
       * would put the browser in charge of which half of a disagreement wins.
       */
      return stated.read ?? benefit
    })

    return { ...policy, benefits }
  } finally {
    clearTimeout(deadline)
    signal?.removeEventListener('abort', stop)
  }
}

/** What the reply proves, checked against the pages. The only thing a person may see. */
export async function verifyDocument(
  doc: LoadedDocument,
  raw: RawPolicy,
  signal?: AbortSignal,
): Promise<ReadOutcome> {
  const response = await call('verify', { doc, raw }, signal)
  if (!response.ok) {
    const said = (await response.json().catch(() => null)) as { error?: string } | null
    throw new Error(said?.error ?? `That reading could not be checked (${response.status}).`)
  }
  return (await response.json()) as ReadOutcome
}

/**
 * Propose and verify, the plan having already been asked for.
 *
 * `planDocument` is called by the screen rather than from in here, because its
 * answer now includes the document-quality gate and a document the gate marked
 * is one somebody should see before a dozen model calls are spent reading it.
 * Framework section 5.2 puts those checks before extraction; this is what
 * leaves room for them to be acted on.
 */
export async function readDocument(
  doc: LoadedDocument,
  signal?: AbortSignal,
): Promise<ReadOutcome> {
  return verifyDocument(doc, await proposePolicy(doc, signal), signal)
}
